import { useMemo } from "react";

import { useMyShopifyDomainSelector } from "@vendor-app/app/_state/reducers/shopify";
import { useSmartrrVendorSelector } from "@vendor-app/app/_state/typedVendorReduxHooks";
import { useIsSuperUserSelector } from "@vendor-app/app/_state/reducers/auth";

import { AccountPlanStatusEnum, FeatureEnum } from "@smartrr/shared/entities/AccountPlan";
import { adminRoutePrefix, adminConfigRoutePrefix, shopUrlPropertyName } from "@smartrr/shared/constants";
import { stringify } from "qs";

import { NavigationHomeIcon } from "@vendor-app/app/AdminRoute/components/layout/icons/navigation/Home";
import { NavigationSubscriptionIcon } from "@vendor-app/app/AdminRoute/components/layout/icons/navigation/Subscription";
import { NavigationCustomerIcon } from "@vendor-app/app/AdminRoute/components/layout/icons/navigation/Customer";
import { NavigationReportIcon } from "@vendor-app/app/AdminRoute/components/layout/icons/navigation/Report";
import { NavigationSubscriptionProgramIcon } from "@vendor-app/app/AdminRoute/components/layout/icons/navigation/SubscriptionProgram";
import { NavigationLoyaltyIcon } from "@vendor-app/app/AdminRoute/components/layout/icons/navigation/Loyalty";
import { NavigationThemingIcon } from "@vendor-app/app/AdminRoute/components/layout/icons/navigation/Theming";
import { NavigationTranslationsIcon } from "@vendor-app/app/AdminRoute/components/layout/icons/navigation/Translations";
import { NavigationIntegrationsIcon } from "@vendor-app/app/AdminRoute/components/layout/icons/navigation/Integrations";
import { NavigationAccountIcon } from "@vendor-app/app/AdminRoute/components/layout/icons/navigation/Account";
import { NavigationNotificationsIcon } from "@vendor-app/app/AdminRoute/components/layout/icons/navigation/Notifications";
import { NavigationGettingStartedIcon } from "@vendor-app/app/AdminRoute/components/layout/icons/navigation/GettingStarted";

import { HeartMajor, QuestionMarkInverseMajor } from "@shopify/polaris-icons";

import { SectionStructure } from "@vendor-app/app/AdminRoute/components/layout/AdminLayoutAndNavigation";
import { useSmartrrFlags } from "@smartrr/shared/LaunchDarkly";
import { useIsAccountSetupCompletedSelector } from "@vendor-app/app/_state/reducers/organizations";

export const useNavigationSections = (): SectionStructure[] => {
  const myShopifyDomain = useMyShopifyDomainSelector();
  const activePlan = useSmartrrVendorSelector(state => state.accountPlans.activePlan);
  const isPlanActive = activePlan?.status === AccountPlanStatusEnum.ACTIVE;
  const isSuperUser = useIsSuperUserSelector();
  const isAccountSetupCompleted = useIsAccountSetupCompletedSelector();
  const {
    ordersTab: ordersTabFeatureFlag,
    bulkUpdatesFeatureFlag,
    displayHomePage: displayHomePageFeatureFlag,
    vendorPortalGettingStartedPage: gettingStartedPageEnabled,
    retentionSubPages: retentionSubPagesFlag,
  } = useSmartrrFlags();

  return useMemo((): SectionStructure[] => {
    const queryString = stringify(
      {
        [shopUrlPropertyName]: myShopifyDomain,
      },
      {
        addQueryPrefix: true,
      }
    );

    const buildUrl = (path: string): string => {
      const url = new URL(`${adminConfigRoutePrefix}${path}`, window.location.origin);

      if (typeof myShopifyDomain === "string") {
        url.searchParams.set(shopUrlPropertyName, myShopifyDomain);
      }

      return `${url.pathname}${url.search}`;
    };

    const filteredSubscriptionNavItems = [
      {
        label: "Orders",
        url: `${adminRoutePrefix}/orders${queryString}`,
        feature: FeatureEnum.CORE_SUBSCRIPTIONS,
        isEnabled: ordersTabFeatureFlag,
      },
      {
        label: "Transactions",
        url: `${adminRoutePrefix}/bills${queryString}`,
        feature: FeatureEnum.CORE_SUBSCRIPTIONS,
        isEnabled: true,
      },
      {
        label: "Bulk Updates",
        url: `${adminRoutePrefix}/bulk-updates${queryString}`,
        feature: FeatureEnum.CORE_SUBSCRIPTIONS,
        isEnabled: bulkUpdatesFeatureFlag,
      },
    ].filter(item => item.isEnabled);

    return [
      {
        items: [
          ...(gettingStartedPageEnabled && !isAccountSetupCompleted
            ? [
                {
                  label: "Getting Started",
                  icon: NavigationGettingStartedIcon,
                  url: `${adminRoutePrefix}/getting-started`,
                  feature: FeatureEnum.CORE_SUBSCRIPTIONS,
                },
              ]
            : []),
          ...(displayHomePageFeatureFlag
            ? [
                {
                  label: "Home",
                  icon: NavigationHomeIcon,
                  url: `${adminRoutePrefix}/home${queryString}`,
                  feature: FeatureEnum.HOME,
                },
              ]
            : []),
          {
            label: "Subscriptions",
            icon: NavigationSubscriptionIcon,
            url: `${adminRoutePrefix}/subscriptions${queryString}`,
            feature: FeatureEnum.CORE_SUBSCRIPTIONS,
            isDefault: !!activePlan,
            subNavigationItems: filteredSubscriptionNavItems,
          },
          {
            label: "Customers",
            icon: NavigationCustomerIcon,
            url: `${adminRoutePrefix}/customers${queryString}`,
            feature: FeatureEnum.CORE_SUBSCRIPTIONS,
          },
          {
            label: "Reports",
            icon: NavigationReportIcon,
            url: `${adminRoutePrefix}/analytics${queryString}`,
            feature: FeatureEnum.ANALYTICS,
          },
          {
            label: "Advanced Analytics",
            icon: NavigationReportIcon,
            url: `${adminRoutePrefix}/advanced-analytics${queryString}`,
            feature: FeatureEnum.LOOKER_ANALYTICS,
          },
        ],
      },
      {
        title: "Configuration",
        items: [
          {
            label: "Subscription Programs",
            icon: NavigationSubscriptionProgramIcon,
            url: `${adminConfigRoutePrefix}/plans${queryString}`,
            feature: FeatureEnum.CORE_CONFIGURATION,
            subNavigationItems: [
              {
                label: "Shipping",
                url: `${adminConfigRoutePrefix}/delivery-profiles${queryString}`,
                feature: FeatureEnum.CORE_CONFIGURATION,
              },
              {
                label: "Transaction Settings",
                url: `${adminConfigRoutePrefix}/billing-settings${queryString}`,
                feature: FeatureEnum.CORE_CONFIGURATION,
              },
              ...(retentionSubPagesFlag === true
                ? []
                : [
                    {
                      label: "Retention",
                      url: buildUrl("/churn-prevention"),
                      feature: FeatureEnum.RETENTION,
                    },
                  ]),
              {
                label: "Bundles",
                url: `${adminConfigRoutePrefix}/bundles${queryString}`,
                feature: FeatureEnum.BUNDLES,
              },
            ],
          },
          ...(retentionSubPagesFlag === true
            ? [
                {
                  label: "Retention",
                  icon: HeartMajor,
                  url: buildUrl("/retention/cancel-reasons"),
                  feature: FeatureEnum.RETENTION,
                  subNavigationItems: [
                    {
                      label: "Cancel Reasons",
                      url: buildUrl("/retention/cancel-reasons"),
                      feature: FeatureEnum.RETENTION,
                    },
                    {
                      label: "Cancellation Intercept",
                      url: buildUrl("/retention/cancellation-intercept"),
                      feature: FeatureEnum.RETENTION,
                    },
                    {
                      label: "Pause Reasons",
                      url: buildUrl("/retention/pause-reasons"),
                      feature: FeatureEnum.RETENTION,
                    },
                  ],
                },
              ]
            : []),
          {
            label: "Loyalty",
            icon: NavigationLoyaltyIcon,
            url: `${adminRoutePrefix}/loyalty${queryString}`,
            feature: FeatureEnum.LOYALTY,
          },
          {
            label: "Customer Notifications",
            icon: NavigationNotificationsIcon,
            url: `${adminConfigRoutePrefix}/subscriber-engagement${queryString}`,
            feature: FeatureEnum.CORE_CONFIGURATION,
          },
          {
            label: "Theming",
            icon: NavigationThemingIcon,
            url: `${adminConfigRoutePrefix}/theming${queryString}`,
            feature: FeatureEnum.CORE_CONFIGURATION,
          },
          {
            label: "Translations",
            icon: NavigationTranslationsIcon,
            url: `${adminConfigRoutePrefix}/translations${queryString}`,
            feature: FeatureEnum.TRANSLATIONS,
          },
          {
            label: "Integrations",
            icon: NavigationIntegrationsIcon,
            url: `${adminConfigRoutePrefix}/integrations${queryString}`,
            feature: FeatureEnum.INTEGRATIONS,
          },
        ],
      },
      {
        title: "Resources",
        items: [
          {
            label: "Account",
            icon: NavigationAccountIcon,
            isDefault: !activePlan, // TODO: Set is default flag to homepage after release
            url: `${adminRoutePrefix}/smartrr-account${queryString}`,
            subNavigationItems: [
              ...((isPlanActive || isSuperUser) &&
              (!gettingStartedPageEnabled || (gettingStartedPageEnabled && isAccountSetupCompleted))
                ? [
                    {
                      label: "Setup",
                      url: `${adminRoutePrefix}/setup${queryString}`,
                      feature: FeatureEnum.SETUP,
                    },
                  ]
                : []),
              ...(isSuperUser
                ? [
                    {
                      label: "Migrations",
                      url: `${adminConfigRoutePrefix}/migrations${queryString}`,
                    },
                  ]
                : []),
              ...(isSuperUser
                ? [
                    {
                      label: "Super User Admin",
                      url: `${adminRoutePrefix}/super-user${queryString}`,
                    },
                  ]
                : []),
            ],
          },
          {
            label: "Help",
            icon: QuestionMarkInverseMajor,
            url: `${adminRoutePrefix}/support${queryString}`,
            feature: FeatureEnum.SETUP,
          },
        ],
      },
    ];
  }, [
    myShopifyDomain,
    activePlan,
    ordersTabFeatureFlag,
    bulkUpdatesFeatureFlag,
    displayHomePageFeatureFlag,
    retentionSubPagesFlag,
  ]);
};
